import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAllMovements, getCashSessions, withdrawCash, getCashSession } from '../../services/CashService';
import { WithdrawModal } from '../../components/CashRegister/WithdrawModal';
import { api } from '../../utils/api';
import type { Movement } from '../../types/@types/cash';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  DollarSign,
  Calendar,
  Search,
  Download,
  Filter,
  CreditCard,
  Banknote,
  Wallet,
  TrendingUp,
  TrendingDown,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { cn } from '../../utils/theme';
import {
  exportSessionsToExcel,
  exportMovementsToExcel,
  exportFullCashReportToExcel,
} from '../../utils/excelExport';

export default function CashMovements() {
  const [activeTab, setActiveTab] = useState<'movements' | 'sessions'>('movements');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'oldest' | 'highestAmount' | 'lowestAmount'>('recent');
  const [orderTypeFilter, setOrderTypeFilter] = useState<'all' | 'TAKEAWAY' | 'MESA'>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'all' | 'sale' | 'withdraw' | 'expense'>('all');

  const { data: movements = [], isLoading, error } = useQuery<Movement[]>({
    queryKey: ['cashMovements'],
    queryFn: getAllMovements,
  });

  const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['cash-sessions'],
    queryFn: () => getCashSessions(1, true),
    staleTime: 60_000,
  });

  const sessions = useMemo(() => sessionsData?.data ?? [], [sessionsData]);

  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  const queryClient = useQueryClient();

  const handleOpenWithdrawModal = async () => {
    try {
      const sess = await getCashSession();
      if (!sess) {
        toast.warn('⚠️ No hay sesión de caja abierta actualmente. Abre la caja antes de registrar retiros.');
        return;
      }
      setShowWithdrawModal(true);
    } catch {
      toast.error('No se pudo verificar el estado de la caja');
    }
  };

  const handleConfirmWithdraw = async (
    amount: number,
    reason: string,
    kind?: string,
    reference?: string,
    openDrawer?: boolean
  ) => {
    setWithdrawing(true);
    try {
      await withdrawCash({
        amount,
        reason,
        kind,
        reference,
      });

      if (openDrawer) {
        api.post('/print-jobs/enqueue', { type: 'drawer' }).catch(() => {});
      }

      toast.success(`Retiro por $${amount.toLocaleString('es-CO')} registrado con éxito`);
      setShowWithdrawModal(false);
      queryClient.invalidateQueries({ queryKey: ['cashMovements'] });
      queryClient.invalidateQueries({ queryKey: ['cash-sessions'] });
      window.dispatchEvent(new Event('cash:updated'));
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al registrar el retiro';
      toast.error(msg);
    } finally {
      setWithdrawing(false);
    }
  };

  const uniqueMethods = useMemo(() => {
    const methods = new Set<string>();
    movements.forEach(m => {
      if (m.method) {
        methods.add(m.method.toUpperCase());
      }
    });
    return Array.from(methods);
  }, [movements]);

  const dateFilteredMovements = useMemo(() => {
    let filtered = movements;
    const now = new Date();
    if (dateFilter === 'today') {
      filtered = filtered.filter(
        (m) => new Date(m.createdAt).toDateString() === now.toDateString()
      );
    } else if (dateFilter === 'week') {
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter((m) => new Date(m.createdAt) >= oneWeekAgo);
    } else if (dateFilter === 'month') {
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter((m) => new Date(m.createdAt) >= oneMonthAgo);
    }
    return filtered;
  }, [movements, dateFilter]);

  const stats = useMemo(() => {
    const sales = dateFilteredMovements.filter((m) => m.type === 'sale');
    const income = sales.reduce((acc, m) => acc + m.amount, 0);
    const outcome = dateFilteredMovements
      .filter((m) => m.type === 'withdraw' || m.type === 'expense')
      .reduce((acc, m) => acc + m.amount, 0);

    const takeawayIncome = sales
      .filter((m) => m.order?.destination === 'TAKEAWAY')
      .reduce((acc, m) => acc + m.amount, 0);

    const tableIncome = sales
      .filter((m) => m.order?.destination === 'MESA')
      .reduce((acc, m) => acc + m.amount, 0);

    return { income, outcome, balance: income - outcome, takeawayIncome, tableIncome };
  }, [dateFilteredMovements]);

  const filteredMovements = useMemo(() => {
    let filtered = dateFilteredMovements;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.orderId?.toLowerCase().includes(lower) ||
          m.reason?.toLowerCase().includes(lower) ||
          m.method?.toLowerCase().includes(lower)
      );
    }

    if (orderTypeFilter !== 'all') {
      filtered = filtered.filter((m) => m.order?.destination === orderTypeFilter);
    }

    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter((m) => m.method?.toUpperCase() === paymentMethodFilter);
    }

    if (transactionTypeFilter !== 'all') {
      filtered = filtered.filter((m) => m.type === transactionTypeFilter);
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      if (sortBy === 'highestAmount') {
        return b.amount - a.amount;
      }
      if (sortBy === 'lowestAmount') {
        return a.amount - b.amount;
      }
      return 0;
    });

    return filtered;
  }, [dateFilteredMovements, searchTerm, orderTypeFilter, paymentMethodFilter, transactionTypeFilter, sortBy]);

  const typeConfig: Record<string, { label: string; icon: React.ElementType; color: string; bg: string; darkBg: string }> = {
    sale: { label: 'Venta', icon: ArrowUpCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', darkBg: 'dark:bg-emerald-900/20' },
    withdraw: { label: 'Retiro', icon: ArrowDownCircle, color: 'text-rose-600', bg: 'bg-rose-50', darkBg: 'dark:bg-rose-900/20' },
    expense: { label: 'Gasto', icon: Wallet, color: 'text-amber-600', bg: 'bg-amber-50', darkBg: 'dark:bg-amber-900/20' },
  };

  const methodIcons: Record<string, React.ElementType> = {
    EFECTIVO: Banknote,
    TARJETA: CreditCard,
    TRANSFERENCIA: DollarSign,
  };

  const dateFilterLabels = {
    all: 'Todos',
    today: 'Hoy',
    week: 'Esta Semana',
    month: 'Este Mes',
  };

  const handleExportActiveTab = () => {
    setIsExporting(true);
    try {
      if (activeTab === 'sessions') {
        if (sessions.length === 0) {
          toast.info('No hay sesiones registradas para exportar');
          return;
        }
        exportSessionsToExcel(sessions);
        toast.success('Reporte Excel de arqueos descargado con éxito');
      } else {
        if (filteredMovements.length === 0) {
          toast.info('No hay movimientos que coincidan con los filtros');
          return;
        }
        exportMovementsToExcel(filteredMovements);
        toast.success('Reporte Excel de movimientos descargado con éxito');
      }
    } catch (err) {
      toast.error('Error al generar el archivo Excel');
    } finally {
      setIsExporting(false);
      setShowExportMenu(false);
    }
  };

  const handleExportFullBook = () => {
    setIsExporting(true);
    try {
      if (sessions.length === 0 && filteredMovements.length === 0) {
        toast.info('No hay datos para exportar');
        return;
      }
      exportFullCashReportToExcel(sessions, filteredMovements);
      toast.success('Libro completo de caja descargado en Excel (2 hojas)');
    } catch (err) {
      toast.error('Error al generar el archivo Excel');
    } finally {
      setIsExporting(false);
      setShowExportMenu(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-16">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <p className="text-sm text-text-muted">Cargando movimientos…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-4 mt-6 rounded-2xl bg-rose-50 p-6 text-center dark:bg-rose-900/20">
        <ArrowDownCircle className="mx-auto mb-2 text-rose-500" size={32} />
        <p className="font-semibold text-rose-700 dark:text-rose-400">Error al cargar los movimientos</p>
        <p className="mt-1 text-sm text-rose-500">Por favor intenta recargar la página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text sm:text-3xl">Caja</h1>
          <p className="mt-0.5 text-sm text-text-muted">Movimientos y arqueos de sesiones</p>
        </div>

        {/* ── Actions ── */}
        <div className="flex items-center gap-2 flex-wrap self-start sm:self-auto">
          <button
            type="button"
            onClick={handleOpenWithdrawModal}
            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 hover:bg-rose-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-rose-600/20 transition active:scale-95"
          >
            <ArrowDownCircle size={18} />
            <span>Registrar Retiro</span>
          </button>

          {/* ── Export Button with Options ── */}
          <div className="relative inline-flex rounded-xl shadow-md">
          <button
            type="button"
            disabled={isExporting}
            onClick={handleExportActiveTab}
            className="inline-flex items-center gap-2 rounded-l-xl bg-emerald-600 hover:bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition active:scale-95 disabled:opacity-50"
            title={activeTab === 'sessions' ? 'Exportar arqueos a Excel' : 'Exportar movimientos a Excel'}
          >
            <FileSpreadsheet size={18} />
            <span>
              {isExporting ? 'Generando Excel...' : activeTab === 'sessions' ? 'Exportar Arqueos (Excel)' : 'Exportar Movimientos (Excel)'}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setShowExportMenu(prev => !prev)}
            className="rounded-r-xl bg-emerald-700 hover:bg-emerald-800 px-2.5 py-2.5 text-white border-l border-emerald-500/40 transition active:scale-95"
            aria-label="Más opciones de exportación"
            title="Opciones de exportación"
          >
            <ChevronDown size={16} />
          </button>

          {showExportMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowExportMenu(false)} />
              <div className="absolute right-0 top-full mt-2 z-50 w-72 rounded-2xl border border-border bg-surface p-2 shadow-2xl text-left text-xs animate-in fade-in zoom-in-95 duration-150">
                <p className="px-3 py-1.5 text-[10px] font-bold text-text-muted uppercase tracking-wider">
                  Descargar Reportes en Excel
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (sessions.length === 0) {
                      toast.info('No hay sesiones registradas');
                    } else {
                      exportSessionsToExcel(sessions);
                      toast.success('Reporte de arqueos generado');
                    }
                    setShowExportMenu(false);
                  }}
                  className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-semibold text-text hover:bg-muted transition text-left"
                >
                  <span className="text-lg">📊</span>
                  <div>
                    <p className="font-bold text-xs text-text">Historial de Arqueos (.xlsx)</p>
                    <p className="text-[10px] text-text-muted">Sesiones de caja, ventas, faltantes y cierres</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (filteredMovements.length === 0) {
                      toast.info('No hay movimientos que coincidan con los filtros');
                    } else {
                      exportMovementsToExcel(filteredMovements);
                      toast.success('Reporte de movimientos generado');
                    }
                    setShowExportMenu(false);
                  }}
                  className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-semibold text-text hover:bg-muted transition text-left"
                >
                  <span className="text-lg">💵</span>
                  <div>
                    <p className="font-bold text-xs text-text">Movimientos de Caja (.xlsx)</p>
                    <p className="text-[10px] text-text-muted">Ventas, retiros y gastos según filtros actuales</p>
                  </div>
                </button>

                <div className="my-1 border-t border-border" />

                <button
                  type="button"
                  onClick={handleExportFullBook}
                  className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-semibold text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition text-left"
                >
                  <span className="text-lg">📑</span>
                  <div>
                    <p className="font-bold text-xs">Libro Completo de Caja (.xlsx)</p>
                    <p className="text-[10px] text-emerald-700/80 dark:text-emerald-400/80">Ambas hojas (Arqueos + Movimientos) en 1 archivo</p>
                  </div>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>

      {/* ── Tab switcher ── */}
      <div className="flex rounded-xl border border-border overflow-hidden text-sm font-semibold w-fit">
        {(['movements', 'sessions'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 transition ${activeTab === tab ? 'bg-primary text-white' : 'text-text-muted hover:bg-muted/40'}`}
          >
            {tab === 'movements' ? 'Movimientos' : 'Historial de sesiones'}
          </button>
        ))}
      </div>

      {activeTab === 'sessions' && <SessionHistory sessions={sessions} isLoading={isLoadingSessions} />}

      {activeTab === 'movements' && <>
      {/* ── Stats Cards ── */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {/* Ingresos */}
        <div className="col-span-1 flex flex-col justify-center gap-1 rounded-2xl border border-border bg-surface px-3 py-3 shadow-sm sm:items-start text-left">
          <div className="flex items-center gap-1.5">
            <div className="rounded-lg bg-emerald-50 p-1 dark:bg-emerald-900/30">
              <TrendingUp className="text-emerald-600" size={12} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">Ingresos</p>
          </div>
          <p className="text-sm font-bold text-emerald-600 sm:text-xl md:text-2xl">
            ${stats.income.toLocaleString()}
          </p>
          <div className="mt-1 hidden w-full justify-between gap-2 text-[10px] text-text-muted sm:flex border-t border-border pt-1 flex-wrap">
            <span>Mesa: ${stats.tableIncome.toLocaleString()}</span>
            <span>Llevar: ${stats.takeawayIncome.toLocaleString()}</span>
          </div>
        </div>

        {/* Egresos */}
        <div className="col-span-1 flex flex-col justify-center gap-1 rounded-2xl border border-border bg-surface px-3 py-3 shadow-sm sm:items-start text-left">
          <div className="flex items-center gap-1.5">
            <div className="rounded-lg bg-rose-50 p-1 dark:bg-rose-900/30">
              <TrendingDown className="text-rose-600" size={12} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">Egresos</p>
          </div>
          <p className="text-sm font-bold text-rose-600 sm:text-xl md:text-2xl">
            ${stats.outcome.toLocaleString()}
          </p>
        </div>

        {/* Balance */}
        <div className="col-span-1 flex flex-col justify-center gap-1 rounded-2xl border border-border bg-surface px-3 py-3 shadow-sm sm:items-start text-left">
          <div className="flex items-center gap-1.5">
            <div className="rounded-lg bg-blue-50 p-1 dark:bg-blue-900/30">
              <Wallet className="text-blue-600" size={12} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">Balance</p>
          </div>
          <p className={cn('text-sm font-bold sm:text-xl md:text-2xl', stats.balance >= 0 ? 'text-blue-600' : 'text-rose-600')}>
            ${stats.balance.toLocaleString()}
          </p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
          <input
            type="text"
            placeholder="Buscar por orden, motivo o método..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-border bg-muted py-2.5 pl-10 pr-4 text-sm text-text placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
          />
        </div>

        {/* Dropdowns grid */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 pt-1">
          {/* Sort Select */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-text-muted uppercase">Ordenar por</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="w-full rounded-xl border border-border bg-muted py-2 px-3 text-xs text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer font-medium"
            >
              <option value="recent">Más recientes</option>
              <option value="oldest">Más antiguos</option>
              <option value="highestAmount">Mayor monto</option>
              <option value="lowestAmount">Menor monto</option>
            </select>
          </div>

          {/* Order Type Select */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-text-muted uppercase">Tipo de Pedido</span>
            <select
              value={orderTypeFilter}
              onChange={(e) => setOrderTypeFilter(e.target.value as any)}
              className="w-full rounded-xl border border-border bg-muted py-2 px-3 text-xs text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer font-medium"
            >
              <option value="all">Todos</option>
              <option value="MESA">Mesa</option>
              <option value="TAKEAWAY">Para llevar</option>
            </select>
          </div>

          {/* Payment Method Select */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-text-muted uppercase">Método de Pago</span>
            <select
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
              className="w-full rounded-xl border border-border bg-muted py-2 px-3 text-xs text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer font-medium"
            >
              <option value="all">Todos</option>
              {uniqueMethods.map((m) => (
                <option key={m} value={m}>
                  {m.charAt(0) + m.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Transaction Type Select */}
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold text-text-muted uppercase">Tipo de Transacción</span>
            <select
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value as any)}
              className="w-full rounded-xl border border-border bg-muted py-2 px-3 text-xs text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer font-medium"
            >
              <option value="all">Todos</option>
              <option value="sale">Venta</option>
              <option value="withdraw">Retiro</option>
              <option value="expense">Gasto</option>
            </select>
          </div>
        </div>

        {/* Date filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 border-t border-border/50 pt-3">
          {(Object.keys(dateFilterLabels) as Array<keyof typeof dateFilterLabels>).map((filter) => (
            <button
              key={filter}
              onClick={() => setDateFilter(filter)}
              className={cn(
                'flex-shrink-0 rounded-xl px-4 py-1.5 text-xs font-semibold transition-all duration-200',
                dateFilter === filter
                  ? 'bg-primary text-primary-fg shadow-md'
                  : 'bg-muted text-text-muted hover:bg-primary/10 hover:text-primary'
              )}
            >
              {dateFilterLabels[filter]}
            </button>
          ))}
        </div>
      </div>

      {/* ── List ── */}
      {filteredMovements.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-surface py-16 text-center shadow-sm">
          <div className="rounded-2xl bg-muted p-4">
            <Filter size={28} className="text-text-muted" />
          </div>
          <div>
            <p className="font-semibold text-text">Sin movimientos</p>
            <p className="mt-0.5 text-sm text-text-muted">Prueba con otros filtros</p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop table (hidden on mobile) */}
          <div className="hidden md:block overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Tipo</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Fecha</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Detalles</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Método</th>
                    <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-text-muted">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredMovements.map((m) => {
                    const config = typeConfig[m.type] || typeConfig['sale'];
                    const Icon = config.icon;
                    const MethodIcon = methodIcons[m.method?.toUpperCase() || ''] || Banknote;
                    return (
                      <tr key={m.id} className="transition-colors hover:bg-muted/40">
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold', config.bg, config.darkBg, config.color)}>
                            <Icon size={13} />
                            {config.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-sm text-text-muted">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={13} />
                            {new Date(m.createdAt).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-text">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{m.orderId ? `Orden #${m.orderId}` : (m.reason || 'Sin descripción')}</span>
                              {m.order?.destination && (
                                <span className={cn(
                                  "inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-semibold",
                                  m.order.destination === 'TAKEAWAY'
                                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                    : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                                )}>
                                  {m.order.destination === 'TAKEAWAY' ? 'Para llevar' : 'Mesa'}
                                </span>
                              )}
                            </div>
                            {m.orderId && <span className="text-xs text-text-muted">Venta registrada</span>}
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-text-muted">
                            <div className="rounded-lg bg-muted p-1.5"><MethodIcon size={13} /></div>
                            <span className="capitalize">{m.method?.toLowerCase() || 'Efectivo'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-right">
                          <span className={cn('text-sm font-bold', m.type === 'sale' ? 'text-emerald-600' : 'text-rose-600')}>
                            {m.type === 'sale' ? '+' : '-'}${m.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards (hidden on desktop) */}
          <div className="space-y-3 md:hidden">
            {filteredMovements.map((m) => {
              const config = typeConfig[m.type] || typeConfig['sale'];
              const Icon = config.icon;
              const MethodIcon = methodIcons[m.method?.toUpperCase() || ''] || Banknote;
              const isIncome = m.type === 'sale';

              return (
                <div key={m.id} className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm transition-all duration-200 active:scale-[0.99]">
                  {/* Card top bar */}
                  <div className={cn('h-1 w-full', isIncome ? 'bg-emerald-500' : (m.type === 'expense' ? 'bg-amber-500' : 'bg-rose-500'))} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      {/* Left: icon + info */}
                      <div className="flex items-start gap-3">
                        <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl', config.bg, config.darkBg)}>
                          <Icon className={config.color} size={20} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="truncate text-sm font-semibold text-text">
                              {m.orderId ? `Orden #${m.orderId}` : (m.reason || 'Sin descripción')}
                            </p>
                            {m.order?.destination && (
                              <span className={cn(
                                "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold",
                                m.order.destination === 'TAKEAWAY'
                                  ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                  : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                              )}>
                                {m.order.destination === 'TAKEAWAY' ? 'Para llevar' : 'Mesa'}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-muted">
                            <Calendar size={11} />
                            {new Date(m.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {/* Right: amount */}
                      <p className={cn('flex-shrink-0 text-base font-bold tabular-nums', isIncome ? 'text-emerald-600' : 'text-rose-600')}>
                        {isIncome ? '+' : '-'}${m.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    {/* Footer */}
                    <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
                      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', config.bg, config.darkBg, config.color)}>
                        <Icon size={10} />
                        {config.label}
                      </span>
                      <div className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-text-muted">
                        <MethodIcon size={10} />
                        <span className="capitalize">{m.method?.toLowerCase() || 'efectivo'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      </> /* end movements tab */}

      <WithdrawModal
        visible={showWithdrawModal}
        onClose={() => setShowWithdrawModal(false)}
        onConfirm={handleConfirmWithdraw}
        loading={withdrawing}
      />
    </div>
  );
}

// ── Session History Component ─────────────────────────────────────────────────

function SessionHistory({ sessions, isLoading }: { sessions: any[]; isLoading: boolean }) {
  const fmt = (n: number) => '$ ' + Math.round(n || 0).toLocaleString('es-CO');
  const fmtDate = (iso?: string) => iso
    ? new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';

  if (isLoading) return (
    <div className="flex h-40 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  return (
    <div className="rounded-2xl border border-border bg-surface shadow-sm overflow-hidden">
      <div className="border-b border-border px-5 py-4">
        <h2 className="font-semibold text-text">Historial de arqueos</h2>
        <p className="text-xs text-text-muted mt-0.5">Últimas {sessions.length} sesiones de caja</p>
      </div>
      <div className="divide-y divide-border">
        {sessions.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-text-muted">
            Sin sesiones registradas aún
          </div>
        ) : sessions.map((s: any) => {
          const diff = s.difference;
          const hasDiff = diff !== null && diff !== undefined;
          return (
            <div key={s.id} className="px-5 py-4 hover:bg-muted/20 transition">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${s.status === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {s.status === 'open' ? 'ABIERTA' : 'CERRADA'}
                    </span>
                    <span className="text-xs text-text-muted">por {s.openedBy ?? 'Desconocido'}</span>
                  </div>
                  <div className="mt-1 text-xs text-text-muted space-y-0.5">
                    <p>Apertura: {fmtDate(s.openedAt)}</p>
                    {s.closedAt && <p>Cierre: {fmtDate(s.closedAt)}</p>}
                    {s.observations && <p className="italic text-text-muted">{s.observations}</p>}
                  </div>
                </div>
                <div className="text-right space-y-1 shrink-0">
                  <div className="text-xs text-text-muted">Apertura: <span className="font-semibold text-text">{fmt(s.initialAmount)}</span></div>
                  <div className="text-xs text-text-muted">Ventas: <span className="font-semibold text-emerald-600">{fmt(s.totalSales)}</span></div>
                  {s.finalAmount != null && <div className="text-xs text-text-muted">Contado: <span className="font-semibold text-text">{fmt(s.finalAmount)}</span></div>}
                  {hasDiff && (
                    <div className={`text-sm font-bold ${Math.abs(diff) < 1000 ? 'text-emerald-600' : diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {diff >= 0 ? '+' : ''}{fmt(diff)}
                      <span className="text-[10px] font-normal ml-1">{Math.abs(diff) < 1000 ? 'cuadrado' : diff > 0 ? 'sobrante' : 'faltante'}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Payment breakdown */}
              {s.paymentBreakdown?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.paymentBreakdown.map((p: any) => (
                    <span key={p.method} className="rounded-full bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-text-muted">
                      {p.method}: {fmt(p.amount)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
